<script lang="ts" setup>
import CategoryForm from '@/components/categories/CategoryForm.vue';
import CategoryHierarchyForm from '@/components/categories/CategoryHierarchyForm.vue';
import ListFilter from '@/components/common/ListFilter.vue';
import TranslatedState from '@/components/common/TranslatedState.vue';
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import { Category } from '@/models/category.model';
import { useEvents, Event } from '@/services/events.composable';
import { Mutations, ShiftCMSState } from '@/store';
import { CategoriesActions, CategoriesState } from '@/stores/categories.store';
import { computed, markRaw, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore<ShiftCMSState & { cs: CategoriesState }>();
const events = useEvents();
const categories = computed<Category[]>(() => store.state.cs.categories);

const addCategory = () => {
  store.commit(Mutations.ShowDrawer, { component: markRaw(CategoryForm) });
};

const editCategory = (_: Event, { item }: { item: Category }) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(CategoryForm),
    data: item,
  });
};

const editHierarchy = () => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(CategoryHierarchyForm),
    small: true,
  });
};

const update = () => {
  store.dispatch(CategoriesActions.GetCategories);
};

onMounted(() => {
  events.on(Event.LanguageChanged, update);
  update();
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, update);
  store.commit(Mutations.HideDrawer);
});

const headers = [
  { title: 'ID', key: 'id', width: '5%' },
  { title: 'Emoji', key: 'emoji', sortable: false, width: '5%' },
  { title: 'Title', key: 'title' },
  { title: 'Type', key: 'type' },
  { title: 'State', key: 'translated', sortable: false },
];

const filterTitle = ref<string>('');
const filterType = ref<string>('');
const filteredCategories = computed(() =>
  categories.value
    .filter((v) =>
      filterType.value === 'main'
        ? !v.parentCategory
        : filterType.value === 'sub'
        ? !!v.parentCategory
        : true,
    )
    .filter((v) =>
      filterTitle.value
        ? v.title.toLowerCase().indexOf(filterTitle.value.toLowerCase()) !== -1
        : true,
    ),
);

function updateFilter(filters: { filter: string; filterTitle: string }) {
  filterType.value = filters.filter;
  filterTitle.value = filters.filterTitle;
}
</script>
<template>
  <v-container>
    <h2>Categories <CurrentLanguage /></h2>
    <ListFilter
      @filter="updateFilter"
      :options="[
        { label: 'Main', value: 'main' },
        { label: 'Sub', value: 'sub' },
      ]"
    />
    <v-data-table
      density="compact"
      @click:row="editCategory"
      :headers="headers"
      :items="filteredCategories.sort((a, b) => a.index - b.index)"
      itemsPerPage="25"
      hover
    >
      <template v-slot:item.type="{ item }">
        {{ !item.parentCategory ? 'Main category' : 'Sub category' }}
      </template>
      <template v-slot:item.translated="{ item }">
        <TranslatedState :item="item" />
      </template>
    </v-data-table>

    <v-btn @click="addCategory" color="success">Add Category</v-btn>
    <v-btn @click="editHierarchy" color="success" class="ml-4"
      >Sort and Hierarchy</v-btn
    >
  </v-container>
</template>
